// CountdownPane — the dormant, read-only screen of a Planner Countdown cook.
//
// Reached when the user enters a sit-down time on the Planner and taps
// Countdown: the plan is anchored backwards from that serve time and held
// dormant here until the calculated cook-start instant arrives, at which point
// Cook.tsx flips into the first live phase (see store `startCountdown` /
// `enterCooking`).
//
// While dormant nothing is cooking, so this screen is locked-until-start
// (DECISIONS — Countdown dormant window): a top banner counts down to the
// cook-start instant and disappears when it arrives; per-phase progress
// indicators are shown but do NOT move (the now-marker parks at each phase's
// start); and the PreCook / PrepCook / Pre/PrepCook / LiveCook chips let the
// user read ahead into each phase exactly as Preview does. The times come from
// the same serve-anchored projection the live cook will use, so the read-ahead
// and the cook can never drift.

import { useMemo, useState } from 'react';
import { Timeline, Wordmark } from '../design/components';
import { theme } from '../design/theme';
import { useAppStore } from '../state/store';
import { PreviewPane } from './PreviewPane';
import { buildPreviewPhases, groupPreviewScreens } from './previewProjection';
import { useNow } from './useNow';
import { correctedNow, formatHHMM, formatHHMMSS } from './helpers';

export function CountdownPane() {
  const recipe = useAppStore((s) => s.recipe);
  const recipeFile = useAppStore((s) => s.recipeFile);
  const serveTime = useAppStore((s) => s.serveTime);
  const cookStartAt = useAppStore((s) => s.cookStartAt);
  const clockOffset = useAppStore((s) => s.clockOffset);

  const now = useNow(clockOffset);

  // Serve-anchored phase projection — identical maths to the live cook. The
  // `nowMs` fallback only matters when no serve time is set, which never
  // happens on the Countdown path.
  const phases = useMemo(
    () =>
      recipe ? buildPreviewPhases(recipe, serveTime, cookStartAt ?? correctedNow(clockOffset)) : [],
    [recipe, serveTime, cookStartAt, clockOffset],
  );
  const screens = useMemo(() => groupPreviewScreens(phases), [phases]);

  const [selected, setSelected] = useState<string>('');
  const activeId = screens.some((s) => s.id === selected) ? selected : (screens[0]?.id ?? '');
  const activeScreen = screens.find((s) => s.id === activeId);

  if (!recipe) return null;

  const remainingSecs =
    cookStartAt != null ? Math.max(0, Math.ceil((cookStartAt - now) / 1000)) : 0;
  const startClock =
    cookStartAt != null ? formatHHMM(new Date(cookStartAt).toISOString()) : '--:--';

  return (
    <main
      aria-label="Countdown"
      style={{
        background: theme.bg.base,
        color: theme.text.primary,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
        padding: '1rem',
      }}
    >
      {/* Countdown-to-start banner. It rides at the top of the screen and is
          removed the instant the cook begins (this whole pane unmounts when
          Cook flips out of the 'countdown' state). No animation — README §7.2. */}
      <div
        role="status"
        aria-live="polite"
        aria-label={`Cooking starts at ${startClock}, in ${formatHHMMSS(remainingSecs)}`}
        style={{
          background: theme.bg.raised,
          color: theme.text.primary,
          padding: '0.75rem 1rem',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'baseline',
          gap: '0.6rem',
          flexWrap: 'wrap',
          minHeight: '44px',
        }}
      >
        <span style={{ fontSize: '1.1rem', fontWeight: 700, color: theme.accent.action }}>
          Cooking starts in {formatHHMMSS(remainingSecs)}
        </span>
        <span style={{ color: theme.text.muted, fontSize: '0.9rem' }}>
          at {startClock} — nothing is cooking yet.
        </span>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: '0.75rem',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.6rem' }}>
          <Wordmark size="sm" as="span" />
          <span style={{ fontSize: '1.2rem', fontWeight: 700 }}>Countdown</span>
          <span style={{ color: theme.text.muted, fontSize: '0.9rem' }}>
            {recipeFile?.name ?? 'Read ahead while the clock runs down.'}
          </span>
        </div>
      </div>

      {/* Phase chips — open each phase's read-ahead screen. */}
      <nav
        aria-label="Countdown phases"
        style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}
      >
        {screens.map((s) => {
          const isActive = s.id === activeId;
          return (
            <button
              key={s.id}
              type="button"
              aria-pressed={isActive}
              onClick={() => setSelected(s.id)}
              style={{
                padding: '0.35rem 0.9rem',
                borderRadius: '999px',
                fontSize: '0.9rem',
                fontWeight: 600,
                lineHeight: 1.2,
                cursor: 'pointer',
                background: isActive ? theme.accent.action : theme.bg.raised,
                color: isActive ? theme.bg.base : theme.text.primary,
                border: '1px solid transparent',
                minHeight: '36px',
              }}
            >
              {s.chipLabel}
            </button>
          );
        })}
      </nav>

      {/* Progress indicators for the ACTIVE screen only — each phase the open
          screen owns (both prep phases on the combined Pre/PrepCook screen,
          otherwise the single phase). The now-marker parks at each phase's
          start (progress 0): the indicators do not move until the cook is live.
          The phase chip sits to the left of each indicator. */}
      {activeScreen && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          {activeScreen.phases.map((p) => (
            <Timeline
              key={`${p.phase}-${p.derived ? 'derived' : 'timed'}`}
              mode={p.phase === 'liveCook' ? 'liveCook' : 'prep'}
              phaseChip={p.label}
              nowLabel={p.windowStart}
              finishLabel={p.windowEnd}
              progress={0}
            />
          ))}
        </div>
      )}

      {activeScreen && <PreviewPane screen={activeScreen} />}
    </main>
  );
}

export default CountdownPane;
